import { createFileRoute, lazyRouteComponent, redirect, type RegisteredRouter } from '@tanstack/react-router';
import { HybridCloudServices } from '@/api/services/hybrid-cloud';
import { head } from '@/router/Head';
import { resolveAccountHasPrivileges } from '@/router/resolvers/user';
import { hybridCloudSearchSchema } from '@/router/utils';

/**
 * Checked against the registered route tree, so a mistyped id fails `tsc` instead of silently never
 * matching. The id is spelled out rather than read off `access.ts`, which takes this route as its
 * parent and so cannot be imported here.
 */
const ACCESS_ROUTE_ID: keyof RegisteredRouter['routesById'] =
  '/_authenticated/_dashboard/accounts/$accountId/hybrid-cloud/access';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/hybrid-cloud')({
  validateSearch: hybridCloudSearchSchema,
  staticData: {
    title: 'Hybrid Cloud',
    // Owns the whole hybrid-cloud subtree (access, create, onboarding, $hybridCloudEnvId, …).
    domain: 'hybrid-cloud',
  },
  beforeLoad: async ({ params: { accountId }, context, matches }) => {
    const hybridCloudEnvsQuery = HybridCloudServices.listHybridCloudEnvironmentsQuery({ accountId });

    // Fetching the environments is the gate for the whole subtree: the permissions interceptor throws an
    // AccessControlError — rendered as "Access Denied" — when the account may not list them. Kept ahead of
    // the privilege redirects below. `listHybridCloudEnvironments` accepts `read:hybrid_cloud_environments`
    // *or* `write:clusters`, so a cluster author gets in too; that is the accepted rule, not an oversight.
    await context.queryClient.fetchQuery(hybridCloudEnvsQuery);

    const hasHybridCloudPrivilege = await resolveAccountHasPrivileges(['HYBRID_CLOUD'], {
      accountId,
      ...context,
    });

    const isAccessSubRoute = matches.some(({ routeId }) => routeId === ACCESS_ROUTE_ID);
    if (isAccessSubRoute) {
      if (hasHybridCloudPrivilege) {
        throw redirect({ to: '/accounts/$accountId/hybrid-cloud', params: { accountId }, replace: true });
      }
    } else if (!hasHybridCloudPrivilege) {
      throw redirect({ to: '/accounts/$accountId/hybrid-cloud/access', params: { accountId }, replace: true });
    }

    return { hybridCloudEnvsQuery };
  },
  loader: ({ context: { queryClient, hybridCloudEnvsQuery } }) => {
    void queryClient.prefetchQuery(hybridCloudEnvsQuery);
  },
  head,
  component: lazyRouteComponent(
    () => import('@/components/HybridCloudEnvironments/HybridCloudPage'),
    'HybridCloudComponent',
  ),
});
